import { useMemo, useEffect, useState, useRef } from 'react';
import './FileUploadProgress.scss';

export interface FileUploadProgressProps {
  fileName: string;
  progress: number;
  stage: 'encrypting' | 'uploading' | 'announcing' | 'hap-downloading' | 'hap-registering' | 'complete' | 'error';
  error?: string;
  onComplete?: (fileName: string) => void;
  onRetry?: (fileName: string) => void;
}

const STAGE_LABELS: Record<FileUploadProgressProps['stage'], string> = {
  encrypting: 'Encrypting file...',
  uploading: 'Storing Locally...',
  announcing: 'Announcing to network...',
  'hap-downloading': 'HAP downloading file...',
  'hap-registering': 'Registering with backend...',
  complete: 'Upload complete',
  error: 'Upload failed'
};

const MAX_PROGRESS = 100;
const FADE_OUT_DELAY = 2000;
const CLEANUP_DELAY = 3000;

const DECIMAL_PRECISION = 100;
const ANIMATION_INTERVAL_MS = 50;
const ANIMATION_INCREMENT = 0.8;
const ENCRYPTING_TARGET = 30;
const UPLOADING_TARGET = 50;

export function FileUploadProgress({ fileName, progress, stage, error, onComplete, onRetry }: FileUploadProgressProps) {
  const stageLabel = useMemo(() => STAGE_LABELS[stage], [stage]);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState(progress);
  const animationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Determine if this stage should animate
  const isAnimatedStage = stage === 'encrypting' || stage === 'uploading';
  const animationTarget = stage === 'encrypting' ? ENCRYPTING_TARGET : stage === 'uploading' ? UPLOADING_TARGET : progress;

  // Handle animated progress for timed stages
  useEffect(() => {
    if (animationRef.current !== null) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }

    if (isAnimatedStage) {
      setAnimatedProgress(progress);
      animationRef.current = setInterval(() => {
        setAnimatedProgress(prev => {
          if (prev >= animationTarget) {
            if (animationRef.current !== null) {
              clearInterval(animationRef.current);
              animationRef.current = null;
            }
            return animationTarget;
          }
          return Math.min(prev + ANIMATION_INCREMENT, animationTarget);
        });
      }, ANIMATION_INTERVAL_MS);
    } else {
      setAnimatedProgress(progress);
    }

    return () => {
      if (animationRef.current !== null) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [stage, progress, isAnimatedStage, animationTarget]);

  const progressPercent = useMemo(() => {
    const clamped = Math.min(MAX_PROGRESS, Math.max(0, animatedProgress));
    return Math.round(clamped * DECIMAL_PRECISION) / DECIMAL_PRECISION;
  }, [animatedProgress]);

  useEffect(() => {
    if (stage === 'complete') {
      const fadeTimer = setTimeout(() => {
        setIsFadingOut(true);
      }, FADE_OUT_DELAY);

      const cleanupTimer = setTimeout(() => {
        if (onComplete != null) {
          onComplete(fileName);
        }
      }, CLEANUP_DELAY);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(cleanupTimer);
      };
    }
    return undefined;
  }, [stage, fileName, onComplete]);

  return (
    <div className={`file-upload-progress ${isFadingOut ? 'file-upload-progress--fading' : ''}`}>
      <div className="file-upload-progress__header">
        <span className="file-upload-progress__filename">{fileName}</span>
        <span className="file-upload-progress__percent">{progressPercent}%</span>
      </div>

      <div className="file-upload-progress__bar-container">
        <div
          className={`file-upload-progress__bar file-upload-progress__bar--${stage}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="file-upload-progress__status">
        <span className={`file-upload-progress__stage file-upload-progress__stage--${stage}`}>
          {stageLabel}
        </span>
        {(error != null && error !== '') && (
          <span className="file-upload-progress__error">{error}</span>
        )}
        {stage === 'error' && onRetry != null && (
          <button
            type="button"
            className="file-upload-progress__retry-button"
            onClick={() => { onRetry(fileName); }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

export default FileUploadProgress;
